import { arrow, autoUpdate, flip, hide, offset, shift, useFloating } from "@floating-ui/react-dom";
import * as React from "react";
import { useEffect, useLayoutEffect, useRef } from "react";
import { fixInitialPosition } from "~/sharedComponents/inlinePopup/middlewares/fixInitialPosition";
import type { InlinePopupProps } from "./InlinePopup";
import { flipBestFitEnhancer } from "./middlewares/flipBestFitEnhancer";

const hiddenStyle: React.CSSProperties = {
    opacity: 0,
    top: -10000,
    left: -10000,
};

export const usePopup = (props: InlinePopupProps) => {
    useWarnAboutFixedToBody(props);
    const { renderToBody, fallbackPlacements } = props;

    const arrowRef = useRef<HTMLDivElement>(null);

    const middlewares = [
        fixInitialPosition(),
        offset(getOffset(props)),
        flip({ fallbackStrategy: "bestFit", fallbackPlacements }),
        flipBestFitEnhancer({ showOverTrigger: props.showOverTrigger }),
        renderToBody ? shift({ crossAxis: true }) : null,
        renderToBody ? hide() : null,
        props.renderArrow ? arrow({ element: arrowRef }) : null,
    ].filter(x => x);

    const currentStrategy = props.strategy ?? (renderToBody ? "fixed" : "absolute");

    const {
        x,
        y,
        placement,
        reference,
        refs,
        strategy,
        update,
        middlewareData: { arrow: { x: arrowX, y: arrowY } = {}, hide: { referenceHidden } = {} },
    } = useFloating({
        strategy: currentStrategy,
        placement: props.placement ?? "bottom-start",
        middleware: middlewares,
    });

    useLayoutEffect(() => {
        reference(props.triggerElRef.current);
    }, [props.triggerElRef.current]);

    useEffect(() => {
        if (!refs.reference.current || !refs.floating.current) {
            return;
        }

        return autoUpdate(refs.reference.current, refs.floating.current, update);
    }, [refs.reference, refs.floating, update]);

    let popupStyle: React.CSSProperties = {
        opacity: 1,
        position: strategy,
        ...getPosition(x, y),
        width: props.width,
        ...(referenceHidden ? hiddenStyle : null),
    };

    return {
        placement,
        popupStyle,
        refs,
        arrowX,
        arrowY,
        arrowRef,
    };
};

const getPosition = (x: number, y: number): React.CSSProperties => {
    const isInitial = x == null && y == null;
    if (isInitial) {
        // If position of popup wasn't calculated yet then render it with fixed position (even if strategy is absolute)
        // because fixed position prevents scrollbars appearing and browser autoscroll to focused element.
        // Fixed position breaks position calculation of popup with absolute strategy, that's fixed by fixInitialPosition middleware.
        return { position: "fixed", top: 0, left: 0, opacity: 0 };
    }

    return { top: y ?? "", left: x ?? "", bottom: "", right: "" };
};

const getOffset = ({ triggerElRef, showOverTrigger, showWithoutIndent }: InlinePopupProps) =>
    showOverTrigger ? -triggerElRef.current?.offsetHeight : showWithoutIndent ? 0 : 4;

const useWarnAboutFixedToBody = ({ strategy, renderToBody }: InlinePopupProps) => {
    if (process.env.NODE_ENV !== "production") {
        useEffect(() => {
            if (strategy === "fixed" && renderToBody) {
                // eslint-disable-next-line no-console
                console.warn(
                    "InlinePopup: 'fixed' strategy is usually used for nested popup, but rendering to body causes clicks inside it to close the parent as outside."
                );
            }
        }, []);
    }
};
